import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Plus, Search, Edit3, Phone, Mail, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const emptyForm = { nome: '', cognome: '', codice_fiscale: '', telefono: '', email: '', indirizzo: '', note: '' };

export const AgenziaClienti = () => {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchClienti = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const res = await api.get(`/agenzia/clienti?${params.toString()}`);
      setClienti(res.data || []);
    } catch {
      toast.error('Errore nel caricamento clienti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { const t = setTimeout(fetchClienti, 250); return () => clearTimeout(t); }, [search]);

  const openNew = () => { setEditId(null); setForm({ ...emptyForm }); setFormOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      nome: c.nome || '', cognome: c.cognome || '', codice_fiscale: c.codice_fiscale || '',
      telefono: c.telefono || '', email: c.email || '', indirizzo: c.indirizzo || '', note: c.note || '',
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) {
      toast.error('Nome e cognome sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/agenzia/clienti/${editId}`, form);
        toast.success('Cliente aggiornato');
      } else {
        await api.post('/agenzia/clienti', form);
        toast.success('Cliente creato');
      }
      setFormOpen(false);
      fetchClienti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clienti Agenzia</h1>
          <p className="text-sm text-slate-500">Anagrafica contatti del modulo CRM Agenzia</p>
        </div>
        <Button onClick={openNew} data-testid="agenzia-nuovo-cliente">
          <Plus size={16} className="mr-2" /> Nuovo Cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Cerca per nome, cognome, CF, telefono..." value={search}
          onChange={(e) => setSearch(e.target.value)} className="pl-9" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Contatti</TableHead>
              <TableHead>Codice Fiscale</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
            ) : clienti.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">Nessun cliente trovato</TableCell></TableRow>
            ) : clienti.map((c) => (
              <TableRow key={c.id} data-testid={`agenzia-cliente-row-${c.id}`}>
                <TableCell className="font-medium text-slate-900">{c.nome} {c.cognome}</TableCell>
                <TableCell className="text-sm text-slate-600">
                  <div className="flex flex-col gap-0.5">
                    {c.telefono && <span className="flex items-center gap-1"><Phone size={12} />{c.telefono}</span>}
                    {c.email && <span className="flex items-center gap-1"><Mail size={12} />{c.email}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{c.codice_fiscale || '-'}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/agenzia/polizze?cliente_id=${c.id}`} title="Vedi polizze">
                      <Button variant="ghost" size="icon"><Shield size={16} /></Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Modifica">
                      <Edit3 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifica Cliente' : 'Nuovo Cliente'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => set('nome', e.target.value)} />
            </div>
            <div>
              <Label>Cognome *</Label>
              <Input value={form.cognome} onChange={(e) => set('cognome', e.target.value)} />
            </div>
            <div>
              <Label>Codice Fiscale</Label>
              <Input value={form.codice_fiscale} onChange={(e) => set('codice_fiscale', e.target.value.toUpperCase())} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.telefono} onChange={(e) => set('telefono', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Indirizzo</Label>
              <Input value={form.indirizzo} onChange={(e) => set('indirizzo', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea rows={3} value={form.note} onChange={(e) => set('note', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
